import {useState} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useNearScreen} from '@s-ui/react-hooks'
import {CardRelatedAdV2} from '@adv-mt/mt-card-related-ad'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import SharedCardsCarousel from '@adv-mt/mt-shared-cards-carousel'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Subhead1} from '@adv-mt/ui/typography'

import TrackingListRelatedAds from '../../../components/TrackingListRelatedAds.js'
import {BASE_CLASS as PAGE_BASE_CLASS} from '../../../config.js'
import useRecommendationWidgetViewed from '../../../hooks/useRecommendationWidgetViewed.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-dealerAds`
const MAX_ITEMS = 10

const DealerAds = ({dealerAds = {}, seller = {}}) => {
  const {domain, i18n} = useSuiContext()
  const {track: analyticsTrack} = useAnalyticsTrack()
  const [isNear, outerRef] = useNearScreen()
  const [products, setProducts] = useState()

  const config = domain.get('config')
  const {DEALER_PACKS} = config.get('dealer')
  const {ADS_DETAIL_PAGE_TYPE, TRACKING_TRANSLATIONS_BY_CULTURE} = config.get('tracking')
  const {ADS_DETAIL_PAGE_NAME: pageNameOrigin, AD_DETAIL_DEALER_ADS_LIST_TYPE: listTypeDealerAds} =
    TRACKING_TRANSLATIONS_BY_CULTURE[i18n.culture]

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {defaultMatches: !config.get('isMobile')})

  const dealerPackType = seller.dealer?.pack
  const isDealerWithPackAdvance = Boolean(seller.isDealer) && dealerPackType === DEALER_PACKS.ADVANCE
  const isDealerWithPackExpert = Boolean(seller.isDealer) && dealerPackType === DEALER_PACKS.EXPERT
  const hasDealerAds = Boolean(dealerAds.items?.length)
  const hasDealerAdsCarousel = (isDealerWithPackAdvance || isDealerWithPackExpert) && hasDealerAds

  useRecommendationWidgetViewed({carouselType: listTypeDealerAds, hasAds: hasDealerAdsCarousel})

  if (!hasDealerAdsCarousel) {
    return null
  }

  const handleClickStock = () =>
    analyticsTrack({
      event: 'Dealer Stock Clicked',
      data: {hit_origin: 'Detail More Vehicles Dealer Stock Clicked'}
    })

  const title = i18n.t('COMMON.MORE_SELLER_VEHICLES')
  const totalResults = i18n.n(Number(dealerAds.totalResults))
  const showAllLabel = `${i18n.t('COMMON.SHOW_ALL')} (${totalResults})`

  return (
    <section className={BASE_CLASS} ref={outerRef}>
      {isNear && (
        <TrackingListRelatedAds ads={dealerAds.items} listType={listTypeDealerAds} onProductsReceived={setProducts} />
      )}
      <SharedCardsCarousel titleNode={<Subhead1 as="h3">{title}</Subhead1>}>
        {dealerAds.items.slice(0, MAX_ITEMS).map((ad, index) => (
          <CardRelatedAdV2
            ad={ad}
            extendedTrackingData={products}
            key={ad.id}
            listType={listTypeDealerAds}
            pageNameOrigin={pageNameOrigin}
            pageType={ADS_DETAIL_PAGE_TYPE}
            position={index}
          />
        ))}
      </SharedCardsCarousel>
      <div className={`${BASE_CLASS}-footerAction`}>
        <Button
          aria-label={`${title}: ${showAllLabel}`}
          fullWidth={!isHigherThanMobile}
          href={seller.dealer.stockPageUrl}
          intent={BUTTON_INTENTS.PRIMARY}
          onClick={handleClickStock}
          shape={BUTTON_SHAPES.CIRCLE}
          size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.OUTLINE}
        >
          {showAllLabel}
        </Button>
      </div>
    </section>
  )
}

DealerAds.displayName = 'DealerAds'

DealerAds.propTypes = {
  dealerAds: PropTypes.shape({
    items: PropTypes.array,
    totalResults: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  seller: PropTypes.shape({
    isDealer: PropTypes.bool,
    dealer: PropTypes.shape({
      pack: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      stockPageUrl: PropTypes.string
    })
  })
}

export default DealerAds
